import React from 'react'
import {
  ListItemText,
  ListItemSecondaryAction,
  Tooltip,
  IconButton, ListItemButton, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { Delete, LibraryBooks, Videocam } from '@mui/icons-material'
import { trackError } from 'services/analytics'
import Condition from 'components/Condition'
import ConfirmButton from 'components/ConfirmButton'
import VideoRecorder from 'components/VideoRecorder'
import { defaultTeleprompterText } from 'components/FreelancerProfile/components/fields/VideoField/utils/defaultTeleprompterText'
import { getErrorText } from 'services/error'
import { useQuickCommit } from 'hooks/useQuickCommit'
import { useVideoRecorder, IUploadedVideoWQuestionData } from 'hooks/video'
import { useSnackbar } from 'hooks/redux'
import { useSubscription } from 'hooks/relay'
import { graphql } from 'relay-runtime'
import { AnswerListItem_VideoUpdatedSubscription } from '__generated__/AnswerListItem_VideoUpdatedSubscription.graphql'
import { AnswerListItem_CreateAnswerMutation } from '__generated__/AnswerListItem_CreateAnswerMutation.graphql'
import { AnswerListItem_DeleteAnswerMutation } from '__generated__/AnswerListItem_DeleteAnswerMutation.graphql'
import { AnswerListItem_DeleteVideoMutation } from '__generated__/AnswerListItem_DeleteVideoMutation.graphql'
import { AnswerListItem_Question$key } from '__generated__/AnswerListItem_Question.graphql'
import { useFragment } from 'react-relay'
import { AnswerListItem_FeaturedFreelancer$key } from '__generated__/AnswerListItem_FeaturedFreelancer.graphql'
import { AnswerListItem_User$key } from '__generated__/AnswerListItem_User.graphql'
import { AnswerListItem_Video$key } from '__generated__/AnswerListItem_Video.graphql'
import { useMediaQuery } from 'hooks/useMediaQuery'
import { Nullable } from 'types'
import { SampleVideosButton_SampleVideos$key } from '__generated__/SampleVideosButton_SampleVideos.graphql'
import QuestionIcon from './components/QuestionIcon'
import TextAnswerDialog from './components/TextAnswerDialog'
import AnswerListItemStatus from './components/AnswerListItemStatus'

export interface IAnswerListItemProps {
  onVideoSaved?: (video: IUploadedVideoWQuestionData) => void
  onError?: (error?: any) => void
  userFragmentRef: AnswerListItem_User$key
  questionFragmentRef?: AnswerListItem_Question$key | null
  title?: string
  featuredFreelancersRef?: AnswerListItem_FeaturedFreelancer$key | null
  index: number
  maxDuration: number
  allowTextualAnswer?: boolean
  finished?: boolean

  /** @deprecated */
  onAnswerSaved?: () => void
}

function AnswerListItem(props: IAnswerListItemProps) {
  const {
    userFragmentRef,
    questionFragmentRef,
    title: titleProp,
    featuredFreelancersRef,
    allowTextualAnswer,
    onVideoSaved,
    onAnswerSaved,
    onError,
    index,
    maxDuration,
    finished = false,
    ...others
  } = props

  const theme = useTheme()
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'))
  const [mobileDialogOpen, setMobileDialogOpen] = React.useState(false)

  const { execute: commitCreateAnswer, loading: creatingAnswer } = useQuickCommit<AnswerListItem_CreateAnswerMutation>(graphql`
    mutation AnswerListItem_CreateAnswerMutation($input: CreateAnswerInput!) {
      createAnswer(input: $input) {
        answer {
          id
          rawId
          textualAnswer
          question {
            id
            title
            textualAnswer {
              id
              rawId
              textualAnswer
              contractRequests {
                status
              }
            }
          }
          contractRequests {
            status
          }
        }
      }
    }
  `)

  const { execute: commitDeleteAnswer, loading: deletingAnswer } = useQuickCommit<AnswerListItem_DeleteAnswerMutation>(graphql`
    mutation AnswerListItem_DeleteAnswerMutation($input: DeleteAnswerInput!) {
      deleteAnswer(input: $input) {
        answer {
          id @deleteRecord
        }
      }
    }
  `)

  const { execute: commitDeleteVideo, loading: deletingVideo } = useQuickCommit<AnswerListItem_DeleteVideoMutation>(graphql`
    mutation AnswerListItem_DeleteVideoMutation($input: DeleteVideoInput!) {
      deleteVideo(input: $input) {
        video {
          id @deleteRecord
        }
      }
    }
  `)

  const user = useFragment(graphql`
    fragment AnswerListItem_User on User {
      configuration {
        videoMaxDuration
        videoMinDuration
      }

      video {
        ...AnswerListItem_Video
      }
    }
  `, userFragmentRef)

  const question = useFragment<AnswerListItem_Question$key>(graphql`
    fragment AnswerListItem_Question on Question {
      rawId
      id
      title
      textualAnswer {
        id
        textualAnswer
      }
      videoAnswer {
        video {
          ...AnswerListItem_Video
        }
      }
    }
  `, questionFragmentRef)

  const featuredFreelancers = useFragment(graphql`
    fragment AnswerListItem_FeaturedFreelancer on User @relay(plural: true) {
      video {
        ...SampleVideosButton_SampleVideos
      }
    }
  `, featuredFreelancersRef)

  const video = useFragment(graphql`
    fragment AnswerListItem_Video on Video {
      id
      url

      ...AnswerListItemStatus_Video
    }
  `, (question ? question.videoAnswer?.video : user?.video) as AnswerListItem_Video$key)

  const { showSnackbarMessage } = useSnackbar()

  const videoId = video?.id
  const sampleVideos = React.useMemo(() => (
    featuredFreelancers?.map(freelancer => freelancer.video) || []
  ), [featuredFreelancers])

  // TODO: this is terrible and needs to be thoroughly cleaned up
  // the video recorder, when finished, should naturally result in the contract being updated on the frontend
  const [newVideoUrl, setNewVideoUrl] = React.useState<Nullable<string>>(null)

  const { videoRecorderProps, isUploading, isVideoRecorderOpen, openVideoRecorder, error: videoRecorderError, uploadProgress } = useVideoRecorder({
    onUploadCompleted: (newVideoData) => {
      setNewVideoUrl(newVideoData.url)

      if (newVideoData?.status === 'processed') {
        showSnackbarMessage('Answer saved', { level: 'success' })
      } else {
        showSnackbarMessage('Your video has been uploaded and is now processing', { level: 'info' })
      }

      if (onVideoSaved) {
        onVideoSaved({ ...newVideoData, question })
      }
    },
    videoParams: {
      questionId: question?.id,
    },
  })

  const handleMobileDialogVideoAnswerClick = () => {
    openVideoRecorder()
    setMobileDialogOpen(false)
  }

  useSubscription<AnswerListItem_VideoUpdatedSubscription>({
    subscription: graphql`
      subscription AnswerListItem_VideoUpdatedSubscription($videoId: ID) {
        videoUpdated(videoId: $videoId) {
          video {
            status
            url

            contractRequests {
              # This is needed to know if the request has been marked as completed following the processing of the video
              status

              jobApplication {
                # Expanding this fragment is important for the job screening flow to work correctly
                ...useScreeningStepsStepper_JobApplication
              }
            }
          }
        }
      }
    `,
    variables: {
      videoId,
    },
  })

  const [textualAnswer, setTextualAnswer] = React.useState<Nullable<string>>(question?.textualAnswer?.textualAnswer)
  const [dialogOpen, setDialogOpen] = React.useState(false)
  const [editError, setEditError] = React.useState<Nullable<Error>>(null)

  const error = editError || videoRecorderError

  const handleAnswerDelete = async (e: React.MouseEvent) => {
    e.stopPropagation()

    const videoAnswer = video || question?.videoAnswer?.video

    try {
      if (videoAnswer) {
        await commitDeleteVideo({
          input: {
            videoId: video?.id,
          },
        })
      }

      if (question?.textualAnswer?.id) {
        await commitDeleteAnswer({
          input: {
            answerId: question?.textualAnswer?.id,
          },
        })
      }
    } catch (err) {
      trackError(err)
      setEditError(new Error(getErrorText(err as Error) || undefined))
    }

    setNewVideoUrl(null)
    setEditError(null)
    showSnackbarMessage('Answer deleted', { level: 'info' })
  }

  const handleTextAnswerClick = (e: React.MouseEvent) => {
    e.stopPropagation()

    setMobileDialogOpen(false)

    if (!isUploading && question && !question?.videoAnswer?.video) {
      setDialogOpen(true)
    }
  }

  const handleItemClick = () => {
    if (isSmallScreen) {
      setMobileDialogOpen(true)
    } else {
      openVideoRecorder()
    }
  }

  const handleRecorderClose = () => {
    setDialogOpen(false)
  }

  const handleDialogSubmit = async () => {
    if (textualAnswer && question?.id) {
      try {
        await commitCreateAnswer({
          input: {
            questionId: question.id,
            textualAnswer,
          },
        })

        showSnackbarMessage('Answer saved', { level: 'success' })

        if (onAnswerSaved) {
          onAnswerSaved()
        }
      } catch (err) {
        setEditError(new Error(getErrorText(err as Error) || undefined))
        trackError(err)

        if (onError) {
          onError(err)
        }
      } finally {
        setDialogOpen(false)
      }
    }
  }

  React.useEffect(() => {
    setTextualAnswer(question?.textualAnswer?.textualAnswer)
  }, [question?.textualAnswer?.textualAnswer])

  const minDuration = user?.configuration?.videoMinDuration ?? 10

  const title = question?.title || titleProp || 'Record a Video'
  const hasVideoAnswer = Boolean(video || newVideoUrl)
  const hasTextAnswer = Boolean(question?.textualAnswer?.textualAnswer && allowTextualAnswer)
  const hasAnswer = hasVideoAnswer || hasTextAnswer
  const canDelete = hasAnswer && !finished
  const isDeleting = deletingAnswer || deletingVideo
  const loading = creatingAnswer || isUploading || isDeleting
  const canRecordVideo = !isUploading && !isDeleting

  let paddingRight = '0px'

  if (!isSmallScreen) {
    paddingRight = hasAnswer ? theme.spacing(32) : theme.spacing(24)
  }

  return (
    <React.Fragment>
      <ListItemButton
        disabled={!canRecordVideo}
        onClick={handleItemClick}
        {...others}
      >
        <QuestionIcon
          hasTextAnswer={hasTextAnswer}
          hasVideoAnswer={hasVideoAnswer}
          hasError={Boolean(error)}
          isWorking={isUploading || isDeleting}
          index={index}
        />

        <ListItemText
          style={{ paddingRight: paddingRight }}
          primary={(
            <div style={{ color: canDelete ? theme.palette.secondary[600] : theme.palette.text.primary, fontWeight: hasAnswer ? 400 : 500 }}>
              {title}
            </div>
          )}
          secondary={(
            <AnswerListItemStatus
              videoFragmentRef={video}
              error={error}
              isUploading={isUploading}
              isTextAnswered={hasTextAnswer}
              maxDuration={maxDuration}
              uploadProgress={uploadProgress}
            />
          )}
        />

        <Condition condition={!isSmallScreen}>
          <ListItemSecondaryAction>
            {canDelete && (
              <Tooltip title="Delete answer">
                <ConfirmButton
                  icon
                  onClick={handleAnswerDelete}
                  color="error"
                  disabled={loading}
                  critical
                  dialogTitle="Delete Answer"
                  dialogMessage={`Delete your answer for the question "${title}"? This cannot be undone.`}
                  dialogConfirmLabel="Delete"
                  style={{ marginRight: theme.spacing(1) }}
                >
                  <Delete />
                </ConfirmButton>
              </Tooltip>
            )}

            {allowTextualAnswer && (
              <Tooltip title="Add text answer">
                <IconButton
                  onClick={handleTextAnswerClick}
                  disabled={Boolean(isUploading || !question || video || newVideoUrl)}
                  data-cy={`answer-question-text-${question?.rawId}`}
                  style={{ marginRight: theme.spacing(1) }}
                >
                  <LibraryBooks />
                </IconButton>
              </Tooltip>
            )}

            <Tooltip title="Add video answer">
              <IconButton
                color="secondary"
                onClick={openVideoRecorder}
                disabled={!canRecordVideo || hasTextAnswer}
                data-cy={question ? `answer-question-video-${question?.rawId}` : 'answer-video-introduction'}
                data-uploading={isUploading}
              >
                <Videocam />
              </IconButton>
            </Tooltip>
          </ListItemSecondaryAction>
        </Condition>
      </ListItemButton>

      {isVideoRecorderOpen && (
        <VideoRecorder
          {...videoRecorderProps}
          title={title}
          sampleVideosFragmentsRefs={sampleVideos as SampleVideosButton_SampleVideos$key} // TODO: figure out why this type assertion is necessary, and fix
          defaultTeleprompterText={question ? null : defaultTeleprompterText}
          defaultVideoURL={newVideoUrl || video?.url}
          maxDuration={maxDuration}
          minDuration={minDuration}
          hideRecordButton={finished}
        />
      )}

      <TextAnswerDialog
        disabled={loading || finished}
        onClose={() => setDialogOpen(false)}
        onCancel={handleRecorderClose}
        onSubmit={handleDialogSubmit}
        onTextualAnswerChange={setTextualAnswer}
        open={dialogOpen}
        title={title}
        textualAnswer={textualAnswer || ''}
      />

      <Dialog open={mobileDialogOpen} onClose={() => setMobileDialogOpen(false)}>
        <DialogTitle>
          Add answer
        </DialogTitle>

        <DialogContent>
          <DialogContentText>
            {title}
          </DialogContentText>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setMobileDialogOpen(false)} color="secondary">
            Cancel
          </Button>

          <Button onClick={handleTextAnswerClick} color="secondary">
            Add text
          </Button>

          <Button onClick={handleMobileDialogVideoAnswerClick} color="primary">
            Add video
          </Button>
        </DialogActions>
      </Dialog>
    </React.Fragment>
  )
}

export default React.memo(AnswerListItem)
